import type { ComponentData, Data } from '@puckeditor/core'
import type { PropOverrides } from './types'
import { getId, isComponentArray } from './_tree'

/**
 * Compute the per-breakpoint override deltas between the base tree and an edited
 * tree. Walks both by component id and records only **changed scalar/object
 * props** — slot/zone arrays (structure) and `id` are skipped, since structure
 * is locked on non-default breakpoints.
 *
 * Returns `{ [componentId]: { [propKey]: value } }` — exactly what's stored in
 * `responsive[bp].props`. Empty when nothing changed.
 */
export function diffPropOverrides(
  base: Data,
  edited: Data,
): Record<string, PropOverrides> {
  const baseById = new Map<string, ComponentData>()
  indexById(base.content ?? [], baseById)
  if (base.zones) {
    Object.values(base.zones).forEach(nodes => indexById(nodes, baseById))
  }

  const result: Record<string, PropOverrides> = {}
  collect(edited.content ?? [], baseById, result)
  if (edited.zones) {
    Object.values(edited.zones).forEach(nodes =>
      collect(nodes, baseById, result),
    )
  }
  return result
}

function indexById(
  nodes: ComponentData[],
  map: Map<string, ComponentData>,
): void {
  for (const node of nodes) {
    const id = getId(node)
    if (id) map.set(id, node)
    forEachSlot(node, child => indexById(child, map))
  }
}

function collect(
  nodes: ComponentData[],
  baseById: Map<string, ComponentData>,
  result: Record<string, PropOverrides>,
): void {
  for (const node of nodes) {
    const id = getId(node)
    const baseNode = id ? baseById.get(id) : undefined
    if (id && baseNode) {
      const delta = diffProps(node.props, baseNode.props)
      if (Object.keys(delta).length) result[id] = delta
    }
    forEachSlot(node, child => collect(child, baseById, result))
  }
}

function diffProps(
  edited: ComponentData['props'],
  base: ComponentData['props'],
): PropOverrides {
  const delta: PropOverrides = {}
  for (const key of Object.keys(edited ?? {})) {
    if (key === 'id') continue
    const value = (edited as Record<string, unknown>)[key]
    if (isComponentArray(value)) continue // structure — not a prop override
    const baseValue = (base as Record<string, unknown> | undefined)?.[key]
    if (!deepEqual(withoutEmpty(value), withoutEmpty(baseValue))) {
      delta[key] = value
    }
  }
  return delta
}

function forEachSlot(
  node: ComponentData,
  fn: (children: ComponentData[]) => void,
): void {
  const props = node.props as Record<string, unknown> | undefined
  if (!props) return
  for (const key of Object.keys(props)) {
    const value = props[key]
    if (isComponentArray(value)) fn(value)
  }
}

/**
 * A prop with its empty parts dropped: `''`, `null`, `undefined` and objects left
 * with no keys all become `undefined`. A field cleared on a breakpoint stores `''`
 * where the base never had the prop, and that must not count as an override, or it
 * stays in `responsive` with no way to remove it (BR-5691 QA). Arrays are kept as
 * they are.
 */
function withoutEmpty(value: unknown): unknown {
  if (value === '' || value === null || value === undefined) return undefined
  if (typeof value !== 'object' || Array.isArray(value)) return value
  const out: Record<string, unknown> = {}
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    const kept = withoutEmpty(child)
    if (kept !== undefined) out[key] = kept
  }
  return Object.keys(out).length ? out : undefined
}

/** Sufficient for Puck props (JSON-serializable). */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  try {
    return JSON.stringify(a) === JSON.stringify(b)
  } catch {
    return false
  }
}
